/**
 * Controller remapping (controls plan C1, the next program's E5): what a shoulder or trigger press
 * fires, from Key Bindings → Gamepad (UserPrefs `controls.gamepadButtons`). Each button fires a bar
 * slot on that bar's current page, or flips a bar's page. With a shift bumper chosen, that bumper
 * fires nothing itself and, held, switches the other buttons to their shifted actions (bar 2 by
 * default), as Shift does on the keyboard.
 */
import { storage } from '../../services/StorageService';
import { USER_PREFS_DEFAULTS, type UserPrefs } from '../../../shared/userprefs/schema';
import { parsePageAction, parseSlotAction, type PageFlip, type SlotPress } from '../../../shared/hotbar/slotActions';
import type { GamepadInputSnapshot } from './GamepadInputSource';

export type PadAbilityButton = 'ability1' | 'ability2' | 'ability3' | 'ability4';
export const PAD_ABILITY_BUTTONS: readonly PadAbilityButton[] = ['ability1', 'ability2', 'ability3', 'ability4'];
export type PadBindingPrefs = Pick<UserPrefs['controls'], 'gamepadButtons' | 'gamepadShiftedButtons' | 'gamepadShiftButton'>;

/** The player's controller bindings, or the defaults when storage cannot be read. */
export function controllerBindingPrefs(): PadBindingPrefs {
  try { return storage.loadUserPrefs().controls; } catch { return USER_PREFS_DEFAULTS.controls; }
}

/** What this frame's pad press fires: a bar slot, a page flip, or nothing (the shift bumper itself). */
export function resolveGamepadPress(
  pad: Pick<GamepadInputSnapshot, 'abilitySlot' | 'abilityHeld'>,
  prefs: PadBindingPrefs = controllerBindingPrefs(),
): { slot?: SlotPress; page?: PageFlip } | undefined {
  if (pad.abilitySlot === undefined) return undefined;
  const button = PAD_ABILITY_BUTTONS[pad.abilitySlot - 1];
  const shift = prefs.gamepadShiftButton;
  if (shift !== 'none' && button === shift) return undefined;
  const action = (shift !== 'none' && pad.abilityHeld[shift] ? prefs.gamepadShiftedButtons : prefs.gamepadButtons)[button];
  const slot = parseSlotAction(action);
  if (slot) return { slot };
  const page = parsePageAction(action);
  return page ? { page } : undefined;
}
